import React from 'react'
import classNames from 'classnames'
import { Classes } from '@blueprintjs/core'
import { RadioGroup, Radio } from '@blueprintjs/core'
import { Select } from 'browser/components/atomic-elements/atoms/select'
import { Input } from 'browser/components/atomic-elements/atoms/input/input'
import { Label } from 'browser/components/atomic-elements/atoms/label/label'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { InputGroupField } from 'browser/components/atomic-elements/molecules/fields/input-group-field/input-group-field'
import {
  EndType,
  Frequency,
  MONTH_OPTIONS,
  getCurrentMonth,
  getTodayWeekday,
  WEEKDAY_OPTIONS,
  DAY_TYPE_OPTIONS,
  DaySelectionMode,
  END_TYPE_OPTIONS,
  parseRRuleString,
  POSITION_OPTIONS,
  FREQUENCY_OPTIONS,
  RecurrenceOptions,
  getTodayDayOfMonth,
  generateRRuleString,
} from './utils'

import 'browser/components/atomic-elements/molecules/fields/recurrence-field/_recurrence-input.scss'

interface IRecurrenceInputProps extends IBaseProps {
  value?: string
  onChange?: (rrule: string | null) => void
  startDateTime?: string
  isDisabled?: boolean
  extraRef?: (ref: any) => void
}

export class RecurrenceInput extends React.Component<IRecurrenceInputProps, RecurrenceOptions> {
  constructor(props: IRecurrenceInputProps) {
    super(props)
    this.state = parseRRuleString(props.value) ?? {
      frequency: 'daily',
      interval: 1,
      endType: 'never',
      count: 10,
      until: '',
      byweekday: [getTodayWeekday()],
      bymonthday: [],
      bymonth: [getCurrentMonth()],
      bysetpos: 1,
      monthlyMode: 'each',
      yearlyMode: 'each',
    }
  }

  componentDidMount() {
    if (!this.props.value) {
      this.emitChange()
    }
  }

  componentDidUpdate(prevProps: IRecurrenceInputProps) {
    if (prevProps.value === this.props.value || !this.props.value) {
      return
    }

    const parsed = parseRRuleString(this.props.value)
    if (parsed) {
      // Protect transient UI state when user has made a choice but the RRULE
      // doesn't fully encode it
      if (this.state.endType === 'until' && !this.state.until) {
        parsed.endType = 'until'
      }
      if (this.state.frequency === 'monthly') {
        parsed.monthlyMode = this.state.monthlyMode
      }
      if (this.state.frequency === 'yearly') {
        parsed.yearlyMode = this.state.yearlyMode
      }
      this.setState(parsed)
    }
  }

  public render() {
    const { className } = this.props
    const { frequency } = this.state

    return (
      <div className={classNames('c-recurrenceInput', className)}>
        {this.renderFrequencySection()}
        {frequency !== 'none' && this.renderIntervalSection()}
        {frequency === 'weekly' && this.renderWeekdaySection()}
        {frequency === 'monthly' && this.renderMonthlySection()}
        {frequency === 'yearly' && this.renderYearlySection()}
        {frequency !== 'none' && this.renderEndsSection()}
      </div>
    )
  }

  private renderFrequencySection() {
    const { isDisabled } = this.props
    const { frequency } = this.state

    return (
      <div className="c-recurrenceInput__section">
        <Label isSecondary={true} className="c-recurrenceInput__sectionLabel">
          Repeats
        </Label>
        <Select
          className="c-recurrenceInput__frequency"
          value={frequency}
          onChange={this.handleFrequencyChange}
          options={FREQUENCY_OPTIONS}
          isDisabled={isDisabled}
        />
      </div>
    )
  }

  private renderIntervalSection() {
    const { isDisabled } = this.props
    const { interval } = this.state

    return (
      <div className="c-recurrenceInput__section">
        <Label isSecondary={true} className="c-recurrenceInput__sectionLabel">
          Every
        </Label>
        <InputGroupField className="c-recurrenceInput__intervalGroup">
          <Input
            className="c-recurrenceInput__interval"
            type="number"
            value={interval}
            onChange={this.handleIntervalChange}
            isDisabled={isDisabled}
          />
          <div className="c-recurrenceInput__intervalUnit">{this.getIntervalLabel()}</div>
        </InputGroupField>
      </div>
    )
  }

  private renderWeekdaySection() {
    const { isDisabled } = this.props
    const { byweekday } = this.state
    const selectedSet = new Set(byweekday)

    return (
      <div className="c-recurrenceInput__section">
        <Label isSecondary={true} className="c-recurrenceInput__sectionLabel">
          On days
        </Label>
        <div className={classNames('c-recurrenceInput__weekdayButtons', Classes.BUTTON_GROUP)}>
          {WEEKDAY_OPTIONS.map(({ value, label }) => (
            <Button
              key={value}
              isActive={selectedSet.has(value)}
              onClick={() => this.handleWeekdayToggle(value)}
              isDisabled={isDisabled}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
    )
  }

  private renderMonthlySection() {
    const { isDisabled } = this.props
    const { monthlyMode } = this.state

    return (
      <div className="c-recurrenceInput__section">
        <RadioGroup
          onChange={(e) => this.handleMonthlyModeChange(e.currentTarget.value as DaySelectionMode)}
          selectedValue={monthlyMode}
          className="c-recurrenceInput__modeToggle"
        >
          <Radio value="each" disabled={isDisabled}>
            <span className="c-recurrenceInput__radioLabel">Each</span>
          </Radio>
          {monthlyMode === 'each' && this.renderMonthDayGrid()}
          <Radio value="onthe" disabled={isDisabled}>
            <span className="c-recurrenceInput__radioLabel">On the</span>
          </Radio>
          {monthlyMode === 'onthe' && this.renderPositionalDaySelector()}
        </RadioGroup>
      </div>
    )
  }

  private renderYearlySection() {
    const { isDisabled } = this.props
    const { bymonth, yearlyMode } = this.state
    const selectedMonths = new Set(bymonth)

    return (
      <>
        <div className="c-recurrenceInput__section">
          <Label isSecondary={true} className="c-recurrenceInput__sectionLabel">
            In
          </Label>
          <div className="c-recurrenceInput__monthGrid">
            {MONTH_OPTIONS.map(({ value, label }) => (
              <Button
                key={value}
                isActive={selectedMonths.has(value)}
                onClick={() => this.handleMonthToggle(value)}
                isDisabled={isDisabled}
                className="c-recurrenceInput__monthButton"
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        <div className="c-recurrenceInput__section">
          <RadioGroup
            onChange={(e) => this.handleYearlyModeChange(e.currentTarget.value as DaySelectionMode)}
            selectedValue={yearlyMode}
            className="c-recurrenceInput__modeToggle"
          >
            <Radio value="each" disabled={isDisabled}>
              <span className="c-recurrenceInput__radioLabel">Each</span>
            </Radio>
            {yearlyMode === 'each' && this.renderMonthDayGrid()}
            <Radio value="onthe" disabled={isDisabled}>
              <span className="c-recurrenceInput__radioLabel">On the</span>
            </Radio>
            {yearlyMode === 'onthe' && this.renderPositionalDaySelector()}
          </RadioGroup>
        </div>
      </>
    )
  }

  private renderMonthDayGrid() {
    const { isDisabled } = this.props
    const { bymonthday } = this.state
    const selectedDays = new Set(bymonthday)

    return (
      <div className="c-recurrenceInput__monthDayGrid">
        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
          <Button
            key={day}
            isActive={selectedDays.has(day)}
            onClick={() => this.handleMonthDayToggle(day)}
            isDisabled={isDisabled}
            className="c-recurrenceInput__monthDayButton"
          >
            {day}
          </Button>
        ))}
      </div>
    )
  }

  private renderPositionalDaySelector() {
    const { isDisabled } = this.props
    const { bysetpos, byweekday } = this.state

    const dayType = this.getDayTypeFromWeekdays(byweekday)

    return (
      <div className="c-recurrenceInput__positionalSelector">
        <Select
          className="c-recurrenceInput__positionSelect"
          value={bysetpos ?? 1}
          onChange={this.handleBysetposChange}
          options={POSITION_OPTIONS}
          isDisabled={isDisabled}
        />
        <Select
          className="c-recurrenceInput__dayTypeSelect"
          value={dayType}
          onChange={this.handleDayTypeChange}
          options={DAY_TYPE_OPTIONS.map(({ value, label }) => ({ value, label }))}
          isDisabled={isDisabled}
        />
      </div>
    )
  }

  private renderEndsSection() {
    const { isDisabled } = this.props
    const { endType, count, until } = this.state

    return (
      <div className="c-recurrenceInput__section">
        <Label isSecondary={true} className="c-recurrenceInput__sectionLabel">
          Ends
        </Label>
        <InputGroupField className="c-recurrenceInput__endsGroup">
          <Select
            className="c-recurrenceInput__endType"
            value={endType}
            onChange={this.handleEndTypeChange}
            options={END_TYPE_OPTIONS}
            isDisabled={isDisabled}
          />
          {endType === 'count' && (
            <>
              <Input
                className="c-recurrenceInput__count"
                type="number"
                value={count}
                onChange={this.handleCountChange}
                isDisabled={isDisabled}
              />
              <div className="c-recurrenceInput__countUnit">occurrences</div>
            </>
          )}
          {endType === 'until' && (
            <Input
              className="c-recurrenceInput__until"
              type="date"
              value={until}
              onChange={this.handleUntilChange}
              isDisabled={isDisabled}
            />
          )}
        </InputGroupField>
      </div>
    )
  }

  private handleFrequencyChange = (value: string) => {
    const frequency = value as Frequency
    const updates: Partial<RecurrenceOptions> = { frequency }

    if (frequency === 'none') {
      this.setState({ ...this.state, ...updates }, this.emitChange)
      return
    }

    if (frequency === 'weekly' && this.state.byweekday.length === 0) {
      updates.byweekday = [getTodayWeekday()]
    }
    if (frequency === 'yearly' && this.state.bymonth.length === 0) {
      updates.bymonth = [getCurrentMonth()]
    }
    if ((frequency === 'monthly' || frequency === 'yearly') && this.state.bymonthday.length === 0) {
      updates.bymonthday = [getTodayDayOfMonth()]
    }

    this.setState({ ...this.state, ...updates }, this.emitChange)
  }

  private handleIntervalChange = (value: number) => {
    this.setState({ interval: Math.max(1, value || 1) }, this.emitChange)
  }

  private handleWeekdayToggle = (day: number) => {
    const { byweekday } = this.state
    const isSelected = byweekday.includes(day)

    if (isSelected && byweekday.length <= 1) {
      return
    }

    const updated = isSelected ? byweekday.filter((d) => d !== day) : [...byweekday, day]

    this.setState({ byweekday: updated }, this.emitChange)
  }

  private handleMonthToggle = (month: number) => {
    const { bymonth } = this.state
    const isSelected = bymonth.includes(month)

    if (isSelected && bymonth.length <= 1) {
      return
    }

    const updated = isSelected ? bymonth.filter((m) => m !== month) : [...bymonth, month]

    this.setState({ bymonth: updated }, this.emitChange)
  }

  private handleMonthDayToggle = (day: number) => {
    const { bymonthday } = this.state
    const isSelected = bymonthday.includes(day)

    if (isSelected && bymonthday.length <= 1) {
      return
    }

    const updated = isSelected ? bymonthday.filter((d) => d !== day) : [...bymonthday, day]

    this.setState({ bymonthday: updated }, this.emitChange)
  }

  private handleMonthlyModeChange = (mode: DaySelectionMode) => {
    const updates: Partial<RecurrenceOptions> = { monthlyMode: mode }

    if (mode === 'each' && this.state.bymonthday.length === 0) {
      updates.bymonthday = [getTodayDayOfMonth()]
    }

    if (mode === 'onthe') {
      if (this.state.byweekday.length === 0) {
        updates.byweekday = [getTodayWeekday()]
      }
      if (this.state.bysetpos === undefined) {
        updates.bysetpos = 1
      }
    }

    this.setState({ ...this.state, ...updates }, this.emitChange)
  }

  private handleYearlyModeChange = (mode: DaySelectionMode) => {
    const updates: Partial<RecurrenceOptions> = { yearlyMode: mode }

    if (mode === 'each' && this.state.bymonthday.length === 0) {
      updates.bymonthday = [getTodayDayOfMonth()]
    }

    if (mode === 'onthe') {
      if (this.state.byweekday.length === 0) {
        updates.byweekday = [getTodayWeekday()]
      }
      if (this.state.bysetpos === undefined) {
        updates.bysetpos = 1
      }
    }

    this.setState({ ...this.state, ...updates }, this.emitChange)
  }

  private handleBysetposChange = (value: number) => {
    this.setState({ bysetpos: value }, this.emitChange)
  }

  private handleDayTypeChange = (value: string) => {
    const option = DAY_TYPE_OPTIONS.find((o) => o.value === value)
    if (option) {
      this.setState({ byweekday: option.weekdays }, this.emitChange)
    }
  }

  private handleEndTypeChange = (value: string) => {
    this.setState({ endType: value as EndType }, this.emitChange)
  }

  private handleCountChange = (value: number) => {
    this.setState({ count: Math.max(1, value || 1) }, this.emitChange)
  }

  private handleUntilChange = (value: string) => {
    this.setState({ until: value }, this.emitChange)
  }

  private emitChange = () => {
    const { onChange, startDateTime } = this.props
    if (onChange) {
      onChange(generateRRuleString(this.state, startDateTime))
    }
  }

  private getIntervalLabel(): string {
    const { frequency, interval } = this.state
    const unit: Record<Frequency, string> = {
      none: '',
      daily: 'day',
      weekly: 'week',
      monthly: 'month',
      yearly: 'year',
    }
    const base = unit[frequency]

    return interval === 1 ? base : base + 's'
  }

  /** Reconstruct a day type selection given a list of weekday numbers (where 0-6 = Mon-Sun) */
  private getDayTypeFromWeekdays(weekdays: number[]): string {
    const inputSet = new Set(weekdays)

    for (const opt of DAY_TYPE_OPTIONS) {
      const sameLength = opt.weekdays.length === inputSet.size
      const allMatch = opt.weekdays.every((day) => inputSet.has(day))
      if (sameLength && allMatch) {
        return opt.value
      }
    }

    return 'monday'
  }
}
